import type { FilterState } from '../../store';
import { parseListingArrayParam } from '../parse-listing-array-param';

type PermalinkEncodedKeys = readonly string[];

/** Filter fields that are mirrored in the address bar. */
export type FilterUrlSyncState = Pick<
  FilterState,
  | 'currentPage'
  | 'sortBy'
  | 'searchQuery'
  | 'selectedFandoms'
  | 'selectedTags'
  | 'selectedUniverses'
  | 'selectedStatuses'
  | 'selectedRatings'
  | 'selectedCharacters'
  | 'selectedPairingCharacters'
>;

/**
 * Pure part of "sync filter state from the address bar": returns the fields that
 * differ from the current state, or null when nothing changed. Dimensions encoded
 * in the permalink path (fandoms/tags/characters/pairingCharacters) are skipped.
 */
export const buildFilterUpdatesFromUrl = (
  searchParams: URLSearchParams,
  filterState: FilterUrlSyncState,
  permalinkEncodedKeys: PermalinkEncodedKeys,
): Partial<FilterState> | null => {
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const urlSort = (searchParams.get('sort') as FilterState['sortBy']) || 'hotNew';
  const urlSearch = searchParams.get('search') || '';
  const urlFandoms = parseListingArrayParam(searchParams.get('fandoms'));
  const urlTags = parseListingArrayParam(searchParams.get('tags'));
  const urlUniverses = parseListingArrayParam(searchParams.get('universes'));
  const urlStatuses = parseListingArrayParam(searchParams.get('statuses'));
  const urlRatings = parseListingArrayParam(searchParams.get('ratings'));
  const urlCharacters = parseListingArrayParam(searchParams.get('characters'));
  const urlPairingCharacters = parseListingArrayParam(searchParams.get('pairingCharacters'));

  const updates: Partial<FilterState> = {};

  if (urlPage !== filterState.currentPage) updates.currentPage = urlPage;
  if (urlSort !== filterState.sortBy) updates.sortBy = urlSort;
  if (urlSearch !== filterState.searchQuery) updates.searchQuery = urlSearch;
  if (
    !permalinkEncodedKeys.includes('fandoms') &&
    JSON.stringify(urlFandoms) !== JSON.stringify(filterState.selectedFandoms)
  ) {
    updates.selectedFandoms = urlFandoms;
  }
  if (
    !permalinkEncodedKeys.includes('tags') &&
    JSON.stringify(urlTags) !== JSON.stringify(filterState.selectedTags)
  ) {
    updates.selectedTags = urlTags;
  }
  if (JSON.stringify(urlUniverses) !== JSON.stringify(filterState.selectedUniverses)) {
    updates.selectedUniverses = urlUniverses;
  }
  if (JSON.stringify(urlStatuses) !== JSON.stringify(filterState.selectedStatuses)) {
    updates.selectedStatuses = urlStatuses;
  }
  if (JSON.stringify(urlRatings) !== JSON.stringify(filterState.selectedRatings)) {
    updates.selectedRatings = urlRatings;
  }
  if (
    !permalinkEncodedKeys.includes('characters') &&
    JSON.stringify(urlCharacters) !== JSON.stringify(filterState.selectedCharacters)
  ) {
    updates.selectedCharacters = urlCharacters;
  }
  if (
    !permalinkEncodedKeys.includes('pairingCharacters') &&
    JSON.stringify(urlPairingCharacters) !== JSON.stringify(filterState.selectedPairingCharacters)
  ) {
    updates.selectedPairingCharacters = urlPairingCharacters;
  }

  return Object.keys(updates).length > 0 ? updates : null;
};
